// @unocss-include
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, clsx, CollapsibleContent, CollapsibleRoot, Icon, Popover, PopoverAnchor, PopoverArrow, PopoverContent, } from '@a-type/ui';
import { useCallback, useEffect, useId, useState } from 'react';
import { proxy, subscribe, useSnapshot } from 'valtio';
import { useEffectOnce } from '../hooks/useEffectOnce.js';
export function createOnboarding(name, steps, startImmediately) {
    var _a;
    const stepUnmounted = {};
    const activeStateStr = typeof window !== 'undefined' ?
        localStorage.getItem(`onboarding-${name}`)
        : null;
    let activeState = startImmediately ? steps[0] : null;
    if (activeStateStr) {
        if (activeStateStr === 'complete')
            activeState = 'complete';
        else
            activeState = (_a = steps.find((step) => step === activeStateStr)) !== null && _a !== void 0 ? _a : null;
    }
    const state = proxy({
        active: activeState,
    });
    subscribe(state, () => {
        if (typeof window !== 'undefined') {
            if (state.active) {
                localStorage.setItem(`onboarding-${name}`, state.active);
            }
            else {
                localStorage.removeItem(`onboarding-${name}`);
            }
        }
    });
    function useBegin() {
        return useCallback(() => {
            if (state.active === null) {
                console.debug('Begin onboarding', name);
                state.active = steps[0];
            }
        }, []);
    }
    function useSkip() {
        return useCallback(() => {
            state.active = 'complete';
        }, []);
    }
    const stepClaims = {};
    function useStep(name, disableNextOnUnmount = false) {
        // a unique ID for this invocation of this hook. useful if there
        // are multiple mounted components using this same hook and name.
        const id = useId();
        const hasClaim = stepClaims[name] === id;
        useEffect(() => {
            if (!stepClaims[name]) {
                stepClaims[name] = id;
                return () => {
                    delete stepClaims[name];
                };
            }
        }, [id]);
        const active = useSnapshot(state).active;
        const next = useCallback(() => {
            if (state.active !== name)
                return;
            const index = steps.indexOf(name);
            if (index === steps.length - 1) {
                state.active = 'complete';
            }
            else {
                state.active = steps[index + 1];
            }
        }, [name]);
        const isLast = steps.indexOf(name) === steps.length - 1;
        const isOnly = steps.length === 1;
        useEffectOnce(() => {
            if (!hasClaim)
                return;
            stepUnmounted[name] = false;
            if (disableNextOnUnmount) {
                return;
            }
            return () => {
                stepUnmounted[name] = true;
                setTimeout(() => {
                    if (stepUnmounted[name]) {
                        next();
                    }
                }, 1000);
            };
        });
        return [active === name && hasClaim, next, isLast, isOnly];
    }
    function useCancel() {
        return useCallback(() => {
            state.active = null;
        }, []);
    }
    return {
        useBegin,
        useSkip,
        useStep,
        useCancel,
        begin: () => {
            if (state.active === null) {
                state.active = steps[0];
            }
        },
        skip: () => {
            state.active = 'complete';
        },
        cancel: () => {
            state.active = null;
        },
    };
}
export function OnboardingBanner({ onboarding, step, children, className, disableNext, }) {
    const [show, next, isLast, isOnly] = onboarding.useStep(step);
    const id = useId();
    return (_jsx(CollapsibleRoot, { open: show, className: clsx('theme-leek', 'w-full', className), children: _jsx(CollapsibleContent, { children: _jsxs("div", { className: "flex flex-col w-full bg-primary-wash color-black p-4 rounded-lg gap-3", children: [_jsx("div", { children: children }), _jsx("div", { className: "flex justify-end gap-3", children: !disableNext && (_jsx(Button, { color: "ghost", onClick: next, children: isLast ?
                                isOnly ?
                                    'Ok'
                                    : 'Finish'
                                : 'Next' })) })] }) }) }));
}
export const OnboardingTooltip = function OnboardingTooltip({ onboarding, step, children, disableNext, content, ignoreOutsideInteraction, }) {
    const [show, next, isLast] = onboarding.useStep(step);
    // delay
    const [delayedOpen, setDelayedOpen] = useState(false);
    useEffect(() => {
        if (show) {
            const timeout = setTimeout(() => {
                setDelayedOpen(true);
            }, 500);
            return () => clearTimeout(timeout);
        }
    }, [show]);
    return (_jsxs(Popover, { open: delayedOpen && show, modal: false, children: [_jsx(PopoverAnchor, { asChild: true, children: children }), _jsxs(PopoverContent, { disableBlur: true, className: clsx('theme-leek', 'bg-primary-wash flex py-2 px-3', 'overflow-visible'), onInteractOutside: (event) => {
                    // if the user interacts outside the popover,
                    // and it's with anything besides a button or input,
                    // go to the next step
                    const target = event.target;
                    if (!ignoreOutsideInteraction || !ignoreOutsideInteraction(target)) {
                        next();
                    }
                }, collisionPadding: 16, children: [_jsx(PopoverArrow, { className: "!fill-primary-wash" }), _jsxs("div", { className: "flex flex-row gap-3 items-center", children: [content, !disableNext && (_jsx(Button, { color: isLast ? 'primary' : 'ghost', size: "small", onClick: next, children: isLast ? 'Finish' : _jsx(Icon, { name: "x" }) }))] })] })] }));
};
//# sourceMappingURL=onboarding.js.map